import { useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import { TimelapseOutlined } from '@mui/icons-material';
import { formatToAmount } from '@qrios/frontend-library';

import locales from '../../../../../../../../constants/locales';
import DetailsList from '../../../../../../../common/details-list';
import WithFormLayout from '../../../../../../../common/with-form-layout';
import DetailsFactory from '../../../../../../../common/details-list/detailsFactory';

import { ClientBalanceContext } from '../../../../../client-balance';
import creditTypesMapperToString from '../../../../../../../../constants/validation/creditTypesToString';
import { WalletCreditInfoResponse } from '../../../../../client-balance/add-funds-to-your-wallet/interfaces';
// eslint-disable-next-line max-len
import { useRejectPendingWalletCreditOrder } from '../../../../../client-balance/reject-pending-wallet-transfer-order/hooks';

interface Props {
  credit: WalletCreditInfoResponse;
  handleClose: () => void;
}

const PendingCredit = ({ credit, handleClose }: Props) => {
  const theme = useTheme();
  const { updateCreditsHistory } = useContext(ClientBalanceContext);
  const { rejectAnOrderLoading, rejectAnOrderError, handleRejectAnOrder } = useRejectPendingWalletCreditOrder();
  const { details } = credit.info;

  const handleSubmit = () => {
    handleRejectAnOrder({
      creditOrderId: credit.info.operationId,
      onSuccess: () => {
        updateCreditsHistory();
        handleClose();
      }
    });
  };

  const detailsDependingOnTheDetailsMessage = () => {
    const { detailsMessage } = credit.info.details;

    if (detailsMessage) {
      return (
        <DetailsList
          items={[
            {
              label: locales.operation.label.transferNarration,
              value: details.transferTitle
            },
            { label: locales.operation.label.accountNumberIban, value: details.bankAccountNumber },
            {
              label: locales.operation.label.transferAmount,
              value: credit.info.creditAmount ? formatToAmount(credit.info.creditAmount.value) : '-'
            },
            {
              label: locales.operation.label.bankName,
              value: details.bankName
            },
            {
              label: locales.operation.label.type,
              value: creditTypesMapperToString(credit.info.details.type)
            },
            {
              label: locales.operation.label.detailsMessage,
              value: credit.info.details.detailsMessage
            }
          ]}
        />
      );
    }

    return (
      <DetailsList
        items={[
          {
            label: locales.operation.label.transferNarration,
            value: details.transferTitle
          },
          { label: locales.operation.label.accountNumberIban, value: details.bankAccountNumber },
          {
            label: locales.operation.label.transferAmount,
            value: credit.info.creditAmount ? formatToAmount(credit.info.creditAmount.value) : '-'
          },
          {
            label: locales.operation.label.bankName,
            value: details.bankName
          },
          {
            label: locales.operation.label.type,
            value: creditTypesMapperToString(credit.info.details.type)
          }
        ]}
      />
    );
  };

  const detailsSection = () => (
    <WithFormLayout
      error={rejectAnOrderError}
      loading={rejectAnOrderLoading}
      danger={true}
      submitLabel="Reject transfer order"
      onSubmit={handleSubmit}
    >
      {detailsDependingOnTheDetailsMessage()}
    </WithFormLayout>
  );

  return (
    <DetailsFactory
      header={{
        icon: { component: TimelapseOutlined, color: theme.palette.warning.main },
        id: credit.info.operationId,
        date: credit.info.attemptDate,
        status: credit.info.creditStatus
      }}
      operationContent={detailsSection()}
    />
  );
};

export default PendingCredit;
